import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import { type Event } from '../lib/api/events';
import { bookingNotificationService } from '@/services/bookingNotificationService';
import { getTimezoneFromCoordinates, convertLocalTimeToUTC } from '@/utils/timezoneUtils';

const bookingFormSchema = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  email: z.string().email(),
  phoneNumber: z.string().min(1),
  salesRepName: z.string().min(1),
  selectedDate: z.string().min(1),
  selectedTime: z.string().min(1),
  levels: z.enum(['One', 'Two', 'Three']),
  atticAccess: z.enum(['In Garage', 'In Bedroom', 'In Hallway']),
  mountType: z.enum(['Roof Mount', 'Ground Mount', 'Both']),
  metersOnSite: z.enum(['One', 'Two', 'Three']),
  electricalPanels: z.enum(['One', 'Two', 'Three']),
  animals: z.enum(['Dogs', 'Cats', 'None']),
  hasBattery: z.enum(['Yes', 'No']),
  requiresConstruction: z.enum(['Yes', 'No']),
  constructionDetails: z.string().optional(),
  installerNote: z.string().optional(),
  address: z.string().min(1),
  streetAddress: z.string().min(1),
  city: z.string().min(1),
  state: z.string().min(1),
  postalCode: z.string().min(1),
  country: z.string().min(1),
  latitude: z.number(),
  longitude: z.number(),
});

type BookingFormData = z.infer<typeof bookingFormSchema>;

interface BookingFormProps {
  event: Event;
  onSubmit?: (data: BookingFormData) => void;
  initialAddress?: {
    address: string;
    street_address: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
    latitude: number;
    longitude: number;
  };
  urlParams?: URLSearchParams;
  projectId?: string; // Added optional projectId
}

function BookingFormGeneral({ event, onSubmit, initialAddress, urlParams, projectId }: BookingFormProps) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [customerTimezone, setCustomerTimezone] = useState<{
    timeZoneId: string;
    timeZoneName: string;
    rawOffset: number;
    dstOffset: number;
  } | null>(null);

  const getDefaultValue = (key: string, defaultVal: string = '') => {
    return urlParams?.get(key) || defaultVal;
  };

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<BookingFormData>({
    resolver: zodResolver(bookingFormSchema),
    defaultValues: {
      address: initialAddress?.address || getDefaultValue('address', ''),
      streetAddress: initialAddress?.street_address || getDefaultValue('streetAddress', ''),
      city: initialAddress?.city || getDefaultValue('city', ''),
      state: initialAddress?.state || getDefaultValue('state', ''),
      postalCode: initialAddress?.postal_code || getDefaultValue('postalCode', ''),
      country: initialAddress?.country || getDefaultValue('country', ''),
      latitude: initialAddress?.latitude || Number(getDefaultValue('latitude', '0')),
      longitude: initialAddress?.longitude || Number(getDefaultValue('longitude', '0')),
      firstName: getDefaultValue('firstName', ''),
      lastName: getDefaultValue('lastName', ''),
      email: getDefaultValue('email', ''),
      phoneNumber: getDefaultValue('phoneNumber', ''),
      salesRepName: getDefaultValue('salesRepName', ''),
      levels: getDefaultValue('levels', '') as 'One' | 'Two' | 'Three',
      atticAccess: getDefaultValue('atticAccess', '') as 'In Garage' | 'In Bedroom' | 'In Hallway',
      mountType: getDefaultValue('mountType', '') as 'Roof Mount' | 'Ground Mount' | 'Both',
      metersOnSite: getDefaultValue('metersOnSite', '') as 'One' | 'Two' | 'Three',
      electricalPanels: getDefaultValue('electricalPanels', '') as 'One' | 'Two' | 'Three',
      animals: getDefaultValue('animals', '') as 'Dogs' | 'Cats' | 'None',
      hasBattery: getDefaultValue('hasBattery', 'No') as 'Yes' | 'No',
      requiresConstruction: getDefaultValue('requiresConstruction', 'No') as 'Yes' | 'No',
      constructionDetails: getDefaultValue('constructionDetails', ''),
      installerNote: getDefaultValue('installerNote', ''),
      selectedDate: getDefaultValue('selectedDate', ''),
      selectedTime: getDefaultValue('selectedTime', ''),
    },
  });

  const requiresConstruction = watch('requiresConstruction');

  useEffect(() => {
    // Set a dummy date for general bookings (2 days from now in customer's timezone)
    const setDummyDate = () => {
      const now = new Date();
      const customerTimezoneId = customerTimezone?.timeZoneId || Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // Create a date 2 days from now in the customer's timezone
      const dummyDate = new Date(now.toLocaleString('en-US', { timeZone: customerTimezoneId }));
      dummyDate.setDate(dummyDate.getDate() + 2);
      dummyDate.setHours(0, 0, 0, 0);
      
      const formatted = dummyDate.toISOString().split('T')[0];
      console.log('Setting dummy date for general booking:', {
        originalDate: now.toString(),
        customerTimezone: customerTimezoneId,
        dummyDate: dummyDate.toString(),
        formatted: formatted
      });
      
      setSelectedDate(formatted);
      setValue('selectedDate', formatted);
      setValue('selectedTime', 'TBD');
    };
    
    setDummyDate();
  }, [setSelectedDate, setValue, customerTimezone]);

  // Fetch customer timezone when address coordinates are available
  useEffect(() => {
    const fetchCustomerTimezone = async () => {
      if (initialAddress?.latitude && initialAddress?.longitude) {
        try {
          const timezone = await getTimezoneFromCoordinates(initialAddress.latitude, initialAddress.longitude);
          setCustomerTimezone(timezone);
          console.log('Customer timezone set (General):', timezone);
        } catch (error) {
          console.error('Error fetching customer timezone:', error);
          // Fallback to user's timezone
          setCustomerTimezone({
            timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
            timeZoneName: 'User Timezone (fallback)',
            rawOffset: 0,
            dstOffset: 0
          });
        }
      }
    };

    fetchCustomerTimezone();
  }, [initialAddress?.latitude, initialAddress?.longitude]);

  const onFormSubmit = async (data: BookingFormData) => {
    try {
      setIsLoading(true);
      if (onSubmit) {
        await onSubmit(data);
      } else {
        // Use stored customer timezone or fallback to user's timezone
        const timezoneToUse = customerTimezone || {
          timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
          timeZoneName: 'User Timezone (fallback)',
          rawOffset: 0,
          dstOffset: 0
        };
        
        console.log('Using timezone for booking (General):', timezoneToUse);
        
        // Create dates in the customer's timezone (project address timezone)
        const [year, month, day] = data.selectedDate.split('-').map(Number);
        const customerDate = new Date(year, month - 1, day, 8, 0); // 8:00 AM in customer's timezone
        
        // Convert the customer's local time to UTC for storage
        // This is the correct approach: customer's local time -> UTC
        const startTime = convertLocalTimeToUTC(customerDate, timezoneToUse.timeZoneId);
        
        const endTime = new Date(startTime);
        endTime.setMinutes(endTime.getMinutes() + (event?.duration || 0));

        console.log('Booking with customer timezone:', {
          startTime: startTime.toISOString(),
          endTime: endTime.toISOString(),
          customerTimezone: timezoneToUse.timeZoneId,
          customerStartTimeLocal: customerDate.toString(),
          customerEndTimeLocal: new Date(customerDate.getTime() + (event?.duration || 0) * 60000).toString()
        });

        // Fetch the project's tenant_id
        let tenantId = null;
        if (projectId) {
          const { data: projectData, error: projectError } = await supabase
            .from('projects')
            .select('tenant_id')
            .eq('id', projectId)
            .single();
          
          if (projectError) {
            console.error('Error fetching project tenant_id:', projectError);
            throw new Error('Failed to fetch project information');
          }
          
          tenantId = projectData?.tenant_id;
        }

        const { data: bookingResult, error: bookingError } = await supabase
          .from('bookings')
          .insert({
            event_id: event.id,
            project_id: projectId,
            tenant_id: tenantId,
            first_name: data.firstName,
            last_name: data.lastName,
            email: data.email,
            phone: data.phoneNumber,
            sales_rep_name: data.salesRepName,
            start_time: startTime.toISOString(),
            end_time: endTime.toISOString(),
            extra_information: {
              ...data,
              address: {
                full: data.address,
                street: data.streetAddress,
                city: data.city,
                state: data.state,
                postalCode: data.postalCode,
                country: data.country,
                coordinates: {
                  latitude: data.latitude,
                  longitude: data.longitude,
                },
              },
              // Add timezone information
              webhookData: {
                start_time: startTime.toISOString(),
                end_time: endTime.toISOString(),
                local_start_time: customerDate.toISOString(),
                local_end_time: new Date(customerDate.getTime() + (event?.duration || 0) * 60000).toISOString(),
                timezone: timezoneToUse.timeZoneId,
                timezone_friendly: timezoneToUse.timeZoneName,
                formatted_date: customerDate.toLocaleDateString('en-US', { 
                  timeZone: timezoneToUse.timeZoneId,
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                }),
                formatted_time: '8:00 AM',
                formatted_datetime: customerDate.toLocaleString('en-US', { 
                  timeZone: timezoneToUse.timeZoneId,
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                })
              }
            },
          })
          .select()
          .single();

        if (bookingError) throw bookingError;

        // Send booking notifications
        if (bookingResult) {
          try {
            await bookingNotificationService.notifyBookingCreated({
              bookingId: bookingResult.id,
              projectId: projectId,
              customerName: `${data.firstName} ${data.lastName}`,
              customerEmail: data.email,
              salesRepName: data.salesRepName,
              bookingDate: data.selectedDate,
              bookingTime: '8:00 AM', // Default time for general bookings
              address: data.address,
              eventName: event.name,
              eventType: event.meeting_type || 'Site Survey',
              customerTimezone: timezoneToUse.timeZoneId,
              metadata: {
                phoneNumber: data.phoneNumber
              }
            });
          } catch (notificationError) {
            console.error('Error sending booking notifications:', notificationError);
            // Don't fail the booking if notifications fail
          }
        }

        toast.success('Booking submitted successfully');

        // Get sales rep email if project exists
        let salesRepEmail = null;
        if (projectId) {
          try {
            const { data: project } = await supabase
              .from('projects')
              .select(`
                sales_rep:users!assigned_sales_rep_id (
                  email,
                  first_name,
                  last_name
                )
              `)
              .eq('id', projectId)
              .single() as { data: { sales_rep: { email: string; first_name: string; last_name: string } | null } | null; error: any };

            salesRepEmail = project?.sales_rep?.email;
          } catch (error) {
            console.warn('Could not fetch sales rep email for confirmation message:', error);
          }
        }

        console.log('Navigating to booking confirmation');
        navigate('/app/booking-confirmation', {
          state: {
            bookingDetails: {
              ...data,
              eventName: event.name,
              meetingType: event.meeting_type,
              meetingLink: event.meeting_link,
              message: "Our team member will reach out to you in next 48 hours for confirming date",
              // Add customer timezone information
              customerTimezone: timezoneToUse.timeZoneId,
              customerTimezoneName: timezoneToUse.timeZoneName,
              customerStartTime: customerDate.toString(),
              customerEndTime: new Date(customerDate.getTime() + (event?.duration || 0) * 60000).toString(),
              // Add sales rep info for confirmation message
              salesRepEmail: salesRepEmail,
              confirmationEmailSentTo: salesRepEmail || data.email
            },

          },
        });
      }
    } catch (error) {
      console.error('Booking submission failed:', error);
      toast.error('Failed to submit booking');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-8">
      <div className="bg-white p-6 rounded-lg shadow-sm space-y-4">
        <h3 className="text-lg font-medium text-foreground">Schedule</h3>
        <p className="text-foreground text-sm">
          For this area, please complete the form below and a member of our team will reach out to the customer directly within 48 hours to schedule their site survey.
        </p>
        <input type="hidden" {...register('selectedDate')} />
        <input type="hidden" {...register('selectedTime')} />
      </div>

      {/* Contact Information Section */}
      <div className="bg-white p-6 rounded-lg shadow-sm space-y-6">
        <h3 className="text-lg font-medium text-foreground">Contact Information</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-foreground">
              First Name
            </label>
            <input
              type="text"
              id="firstName"
              {...register('firstName')}
              className="mt-1 block w-full rounded-md border border-border bg-white p-2 shadow-sm focus:border-primary-500 focus:ring-primary sm:text-sm"
            />
            {errors.firstName && (
              <p className="mt-1 text-sm text-destructive">{errors.firstName.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-foreground">
              Last Name
            </label>
            <input
              type="text"
              id="lastName"
              {...register('lastName')}
              className="mt-1 block w-full rounded-md border border-border bg-white p-2 shadow-sm focus:border-primary-500 focus:ring-primary sm:text-sm"
            />
            {errors.lastName && (
              <p className="mt-1 text-sm text-destructive">{errors.lastName.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-foreground">
              Email
            </label>
            <input
              type="email"
              id="email"
              {...register('email')}
              className="mt-1 block w-full rounded-md border border-border bg-white p-2 shadow-sm focus:border-primary-500 focus:ring-primary sm:text-sm"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-destructive">{errors.email.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="phoneNumber" className="block text-sm font-medium text-foreground">
              Phone Number
            </label>
            <input
              type="tel"
              id="phoneNumber"
              {...register('phoneNumber')}
              className="mt-1 block w-full rounded-md border border-border bg-white p-2 shadow-sm focus:border-primary-500 focus:ring-primary sm:text-sm"
            />
            {errors.phoneNumber && (
              <p className="mt-1 text-sm text-destructive">{errors.phoneNumber.message}</p>
            )}
          </div>

          <div className="sm:col-span-2">
            <label htmlFor="salesRepName" className="block text-sm font-medium text-foreground">
              Sales Representative Name
            </label>
            <input
              type="text"
              id="salesRepName"
              {...register('salesRepName')}
              className="mt-1 block w-full rounded-md border border-border bg-white p-2 shadow-sm focus:border-primary-500 focus:ring-primary sm:text-sm"
            />
            {errors.salesRepName && (
              <p className="mt-1 text-sm text-destructive">{errors.salesRepName.message}</p>
            )}
          </div>
        </div>
      </div>

      {/* Property Details Section */}
      <div className="bg-white p-6 rounded-lg shadow-sm space-y-6">
        <h3 className="text-lg font-medium text-foreground">Property Details</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Number of Levels
            </label>
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  id="levels-one"
                  type="radio"
                  value="One"
                  {...register('levels')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="levels-one" className="ml-3 block text-sm font-medium text-foreground">
                  One
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="levels-two"
                  type="radio"
                  value="Two"
                  {...register('levels')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="levels-two" className="ml-3 block text-sm font-medium text-foreground">
                  Two
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="levels-three"
                  type="radio"
                  value="Three"
                  {...register('levels')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="levels-three" className="ml-3 block text-sm font-medium text-foreground">
                  Three
                </label>
              </div>
            </div>
            {errors.levels && (
              <p className="mt-1 text-sm text-destructive">{errors.levels.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Attic Access Location
            </label>
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  id="attic-garage"
                  type="radio"
                  value="In Garage"
                  {...register('atticAccess')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="attic-garage" className="ml-3 block text-sm font-medium text-foreground">
                  In Garage
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="attic-bedroom"
                  type="radio"
                  value="In Bedroom"
                  {...register('atticAccess')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="attic-bedroom" className="ml-3 block text-sm font-medium text-foreground">
                  In Bedroom
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="attic-hallway"
                  type="radio"
                  value="In Hallway"
                  {...register('atticAccess')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="attic-hallway" className="ml-3 block text-sm font-medium text-foreground">
                  In Hallway
                </label>
              </div>
            </div>
            {errors.atticAccess && (
              <p className="mt-1 text-sm text-destructive">{errors.atticAccess.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Type of Mount
            </label>
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  id="mount-roof"
                  type="radio"
                  value="Roof Mount"
                  {...register('mountType')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="mount-roof" className="ml-3 block text-sm font-medium text-foreground">
                  Roof Mount
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="mount-ground"
                  type="radio"
                  value="Ground Mount"
                  {...register('mountType')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="mount-ground" className="ml-3 block text-sm font-medium text-foreground">
                  Ground Mount
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="mount-both"
                  type="radio"
                  value="Both"
                  {...register('mountType')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="mount-both" className="ml-3 block text-sm font-medium text-foreground">
                  Both
                </label>
              </div>
            </div>
            {errors.mountType && (
              <p className="mt-1 text-sm text-destructive">{errors.mountType.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Meters on Site
            </label>
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  id="meters-one"
                  type="radio"
                  value="One"
                  {...register('metersOnSite')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="meters-one" className="ml-3 block text-sm font-medium text-foreground">
                  One
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="meters-two"
                  type="radio"
                  value="Two"
                  {...register('metersOnSite')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="meters-two" className="ml-3 block text-sm font-medium text-foreground">
                  Two
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="meters-three"
                  type="radio"
                  value="Three"
                  {...register('metersOnSite')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="meters-three" className="ml-3 block text-sm font-medium text-foreground">
                  Three
                </label>
              </div>
            </div>
            {errors.metersOnSite && (
              <p className="mt-1 text-sm text-destructive">{errors.metersOnSite.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Electrical Panels on Site
            </label>
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  id="panels-one"
                  type="radio"
                  value="One"
                  {...register('electricalPanels')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="panels-one" className="ml-3 block text-sm font-medium text-foreground">
                  One
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="panels-two"
                  type="radio"
                  value="Two"
                  {...register('electricalPanels')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="panels-two" className="ml-3 block text-sm font-medium text-foreground">
                  Two
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="panels-three"
                  type="radio"
                  value="Three"
                  {...register('electricalPanels')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="panels-three" className="ml-3 block text-sm font-medium text-foreground">
                  Three
                </label>
              </div>
            </div>
            {errors.electricalPanels && (
              <p className="mt-1 text-sm text-destructive">{errors.electricalPanels.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Animals Present
            </label>
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  id="animals-dogs"
                  type="radio"
                  value="Dogs"
                  {...register('animals')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="animals-dogs" className="ml-3 block text-sm font-medium text-foreground">
                  Dogs
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="animals-cats"
                  type="radio"
                  value="Cats"
                  {...register('animals')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="animals-cats" className="ml-3 block text-sm font-medium text-foreground">
                  Cats
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="animals-none"
                  type="radio"
                  value="None"
                  {...register('animals')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="animals-none" className="ml-3 block text-sm font-medium text-foreground">
                  None
                </label>
              </div>
            </div>
            {errors.animals && (
              <p className="mt-1 text-sm text-destructive">{errors.animals.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Does this project have a battery?
            </label>
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  id="battery-yes"
                  type="radio"
                  value="Yes"
                  {...register('hasBattery')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="battery-yes" className="ml-3 block text-sm font-medium text-foreground">
                  Yes
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="battery-no"
                  type="radio"
                  value="No"
                  {...register('hasBattery')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="battery-no" className="ml-3 block text-sm font-medium text-foreground">
                  No
                </label>
              </div>
            </div>
            {errors.hasBattery && (
              <p className="mt-1 text-sm text-destructive">{errors.hasBattery.message}</p>
            )}
          </div>
        </div>
      </div>

      {/* Construction Section */}
      <div className="bg-white p-6 rounded-lg shadow-sm space-y-6">
        <h3 className="text-lg font-medium text-foreground">Construction</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Does this project require any construction or electrical work?
            </label>
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  id="construction-yes"
                  type="radio"
                  value="Yes"
                  {...register('requiresConstruction')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="construction-yes" className="ml-3 block text-sm font-medium text-foreground">
                  Yes
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="construction-no"
                  type="radio"
                  value="No"
                  {...register('requiresConstruction')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="construction-no" className="ml-3 block text-sm font-medium text-foreground">
                  No
                </label>
              </div>
            </div>
            {errors.requiresConstruction && (
              <p className="mt-1 text-sm text-destructive">{errors.requiresConstruction.message}</p>
            )}
          </div>

          {requiresConstruction === 'Yes' && (
            <div className="sm:col-span-2">
              <label htmlFor="constructionDetails" className="block text-sm font-medium text-foreground">
                Please explain the construction or electrical work needed
              </label>
              <textarea
                id="constructionDetails"
                rows={3}
                {...register('constructionDetails')}
                className="mt-1 block w-full rounded-md border border-border bg-white p-2 shadow-sm focus:border-primary-500 focus:ring-primary sm:text-sm"
                placeholder="Describe the construction or electrical work needed..."
              />
              {errors.constructionDetails && (
                <p className="mt-1 text-sm text-destructive">{errors.constructionDetails.message}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Installer Note Section */}
      <div className="bg-white p-6 rounded-lg shadow-sm space-y-6">
        <h3 className="text-lg font-medium text-foreground">Installer Note</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label htmlFor="installerNote" className="block text-sm font-medium text-foreground">
              Note to Installer (From Sales Rep)
            </label>
            <textarea
              id="installerNote"
              rows={4}
              {...register('installerNote')}
              className="mt-1 block w-full rounded-md border border-border bg-white p-2 shadow-sm focus:border-primary-500 focus:ring-primary sm:text-sm"
              placeholder="Add any special instructions or notes for the installer..."
            />
            {errors.installerNote && (
              <p className="mt-1 text-sm text-destructive">{errors.installerNote.message}</p>
            )}
          </div>
        </div>
      </div>

      <div>
        <button
          type="submit"
          disabled={isLoading}
          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primaryHover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
        >
          {isLoading ? 'Submitting...' : 'Book Appointment'}
        </button>
      </div>
    </form>
  );
}

export default BookingFormGeneral;
